import React, { useContext, useState, useCallback, useEffect } from 'react';
import i18next from 'i18next';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Grid, Box, Tooltip, IconButton } from '@mui/material';
import { Add as AddIcon, HighlightOff } from '@mui/icons-material';

import useStyles from 'style/js-style/containers/administration/rules/RuleFormsStyle';
import EAButton from 'components/common/EAButton';
import {
  RuleActionParameterNames,
  RuleActionType,
  RulesContext,
  getRuleActionModel,
} from 'services/common/RulesUtils';
import RuleSelectInput from 'components/rules/RuleSelectInput';
import EAIcon from '../../../../../components/common/EAIcon';
import DocumentAddAndRemoveActionParams from './document/DocumentAddAndRemoveActionParams';
import DossierStopActionParams from './dossier/DossierStopActionParams';
import ContextAddAndArchiveThirdParty from './context/ContextAddAndArchiveThirdParty';
import DossierCreationActionParams from "./dossier/DossierCreationActionParams";
import DossierTypeChangeActionParams from './dossier/DossierTypeChangeActionParams';
import RuleReverseActionParam from './RuleReverseActionParam';
import DocumentStatusChangeParams from './document/DocumentStatusChangeParams';

const RuleActions = ({ fieldName, ruleType, readOnly = false }) => {
  const { classes } = useStyles();
  const [hoveredLine, setHoveredLine] = useState(null);

  const formMethods = useFormContext();
  const { control } = formMethods;

  const { fields: actions, append, remove } = useFieldArray({
    control,
    name: `${fieldName}`,
  });

  const onAddEntry = useCallback(() => {
    append(getRuleActionModel());
  }, [append]);

  const onRemoveEntry = useCallback(
    (index) => {
      remove(index);
    },
    [remove]
  );

  return (
    <Grid container spacing={2}>
      {actions?.map((item, index) => {
        return (
          <React.Fragment key={index}>
            <Grid item xs={1}>
              <EAIcon
                icon={'rules/actionLine'}
                className={classes.actionLine}
              />
            </Grid>
            <Grid
              item
              xs={11}
              key={index}
              onMouseEnter={() => setHoveredLine(index)}
              onMouseLeave={() => setHoveredLine(null)}
              style={{
                backgroundColor: '#F9F9FB',
                marginBottom: 19,
                paddingBottom: 16,
              }}
            >
              <RuleAction
                fieldName={`${fieldName}.${index}`}
                onRemove={() => onRemoveEntry(index)}
                isHovered={hoveredLine === index}
                index={index}
                ruleType={ruleType}
                readOnly={readOnly}
              />
            </Grid>
          </React.Fragment>
        );
      })}

      {!readOnly &&
        <Grid item xs={12} sx={{ padding: '0 !important' }}>
          <EAButton
            borderless={true}
            className={classes.addChoiceWrapper}
            onClick={(e) => {
              e.preventDefault();
              onAddEntry();
            }}
            content={
              <div className={classes.addChoiceBtn}>
                <AddIcon style={{ width: '16px', marginRight: '7px' }} />
                <span style={{ fontSize: '12px' }}>
                  {i18next.t('rules.configuration.ruleActions.actions.addAction')}
                </span>
              </div>
            }
            customStyle={{
              position: 'relative',
              left: '18px',
            }}
          />
        </Grid>
      }
    </Grid>
  );
};

const RuleAction = ({ fieldName, onRemove, isHovered, index, ruleType, readOnly = false }) => {
  const { classes } = useStyles();
  const { actionTypes } = useContext(RulesContext);

  const [availableActionTypes, setAvailableActionTypes] = useState([]);

  const formMethods = useFormContext();
  const { setValue, clearErrors } = formMethods;

  const data = useWatch({ name: `${fieldName}` });

  const onActionTypeChange = (newValue) => {
    setValue(`${fieldName}.parameters`, {});
    setValue(
      `${fieldName}.parameters.${RuleActionParameterNames.DOCUMENT_TYPE_IDS}`,
      ''
    );
    clearErrors(`${fieldName}.parameters`, '');
  };

  const renderDeleteButton = () => {
    return (
      <Tooltip
        placement="top"
        title={i18next.t('delete')}
        sx={{ position: 'relative' }}
      >
        <IconButton
          onClick={onRemove}
          className={classes.valuesIconBtnClosed}
          size="large"
        >
          <HighlightOff />
        </IconButton>
      </Tooltip>
    );
  };

  useEffect(() => {
      const matchedTypes = actionTypes.filter((type) => type.target === ruleType);
      setAvailableActionTypes(matchedTypes.length > 0 ? matchedTypes : []);
  }, [actionTypes, ruleType]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2} sx={{}} style={{ alignItems: 'center' }}>
        <Grid item xs={11} md={11}>
          <Grid container spacing={2} sx={{}} style={{ alignItems: 'center' }}>
            <Grid item xs={12} md={3}>
              <RuleSelectInput
                fieldName={`${fieldName}.type`}
                label={`${i18next.t(
                  'rules.configuration.ruleActions.fields.type.label',
                  { indice: index + 1 }
                )}`}
                placeholder={i18next.t(
                  `rules.configuration.ruleActions.fields.type.placeholder`
                )}
                options={availableActionTypes}
                onChange={(newValue) => {
                  onActionTypeChange(newValue);
                }}
                isRequired
                validate={() => {}}
                labelVariant="chip"
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} md={9}>
              <RuleActionParams
                fieldName={`${fieldName}.parameters`}
                actionType={data?.type}
                readOnly={readOnly}
              />
            </Grid>
          </Grid>
        </Grid>

        {!readOnly &&
          <Grid item xs={1} md={1}>
            {isHovered && renderDeleteButton()}
          </Grid>
        }
      </Grid>
    </Box>
  );
};

const RuleActionParams = ({ fieldName, actionType, readOnly = false }) => {
  switch (actionType) {
    case RuleActionType.DOSSIER_CREATION:
      return <DossierCreationActionParams actionType={actionType} fieldName={fieldName} readOnly={readOnly}/>;

    case RuleActionType.DOSSIER_STOP:
      return <DossierStopActionParams fieldName={fieldName} readOnly={readOnly}/>;

    case RuleActionType.THIRD_PARTY_STOP:
      return <></>;

    case RuleActionType.DOCUMENT_ADD:
    case RuleActionType.DOCUMENT_REMOVE:
      return <DocumentAddAndRemoveActionParams actionType={actionType} fieldName={fieldName} readOnly={readOnly}/>;

    case RuleActionType.ADD_THIRD_PARTY_TO_CONTEXT:
    case RuleActionType.ARCHIVE_THIRD_PARTY_FROM_CONTEXT:
      return <ContextAddAndArchiveThirdParty fieldName={fieldName} readOnly={readOnly}/>;

    case RuleActionType.ARCHIVE_CONTEXT_THIRDPARTY:
    case RuleActionType.ARCHIVE_CONTEXT:
    case RuleActionType.ACTIVATE_CONTEXT:
      return <></>

    case RuleActionType.ACTIVATE_CONTEXT_THIRDPARTY:
      return <RuleReverseActionParam fieldName={fieldName} readOnly={readOnly}/>;

    case RuleActionType.ADD_DOSSIER_CONTEXT_THIRDPARTY:
    case RuleActionType.ADD_DOSSIER_CONTEXT:
      return <DossierCreationActionParams actionType={actionType} fieldName={fieldName} readOnly={readOnly}/>;

    case RuleActionType.ARCHIVE_DOSSIER_CONTEXT_THIRDPARTY:
    case RuleActionType.ARCHIVE_DOSSIER_CONTEXT:
      return <DossierStopActionParams fieldName={fieldName} readOnly={readOnly}/>;

    case RuleActionType.DOSSIER_TYPE_CHANGE:
      return <DossierTypeChangeActionParams fieldName={fieldName} readOnly={readOnly}/>;

    case RuleActionType.FINANCIAL_STRENGTH_ACTIVATION:
    case RuleActionType.DNB_ESG_ACTIVATION:
        return <RuleReverseActionParam fieldName={fieldName} readOnly={readOnly}/>;

    case RuleActionType.DOCUMENT_STATUS_CHANGE:
      return <DocumentStatusChangeParams fieldName={fieldName} readOnly={readOnly} />;

    default:
      return <></>;
  }
};

export default RuleActions;
